import * as React from "react"
import {
  Button,
  Spacer,
  InputFieldBlock,
  Text,
  ThemeCss,
  StyledPanelBoilerplate,
  StyledPanelBodySection,
  StyledPanelActions,
} from "gatsby-interface"
import { MdArrowForward } from "react-icons/md"
import {
  ui as uiText,
  notificationDeploys as notificationDeploysText,
} from "@modules/locales/default.js"
import { getNotificationDeploysStatus } from "./utils"
import { useUpsertNotificationIntegration } from "./hooks/useUpsertNotificationIntegration"
import {
  CloudResourceType,
  NotificationEventType,
  NotificationTransport,
} from "@modules/graphql/types"
import { ErrorAlert } from "@modules/ui/components/ErrorAlert"
import { NotificationEventField } from "./NotificationEventField"

const listItemCss: ThemeCss = theme => ({
  marginBottom: theme.space[3],
})

export type WebhookModalProps = {
  onClose: () => void
  isOpen: boolean
  resourceId: string
  resourceType: CloudResourceType
  label: string
  ariaLabel: string
  inputLabel?: string
  inputHint?: string
  transport: NotificationTransport
  specificEvents?: NotificationEventType[]
}

export function WebhookModal({
  onClose,
  isOpen,
  label,
  ariaLabel,
  inputLabel,
  inputHint,
  resourceId,
  resourceType,
  transport,
  specificEvents = [],
}: WebhookModalProps) {
  const [webhookUrl, setWebhookUrl] = React.useState<string>("")
  const [selectedEvent, setSelectedEvent] = React.useState<
    NotificationEventType | ``
  >()

  const [
    upsertNotificationDeploy,
    { loading, error },
  ] = useUpsertNotificationIntegration({ resourceId, resourceType })

  const onSubmit = (events: NotificationEventType[]) => {
    if (events.length === 0) {
      return
    }

    Promise.all(
      events.map(event => {
        return upsertNotificationDeploy({
          resourceId,
          resourceType,
          transport,
          event: event as NotificationEventType,
          config: webhookUrl ? { url: webhookUrl } : {},
        })
      })
    ).then(onClose)
  }

  const hasSpecificEvents = specificEvents.length > 0
  const disableSubmit =
    (inputLabel && !webhookUrl) || (!selectedEvent && !hasSpecificEvents)

  return (
    <StyledPanelBoilerplate
      isOpen={isOpen}
      aria-label={ariaLabel}
      onClose={onClose}
      header={label}
    >
      <StyledPanelBodySection>
        {inputLabel && (
          <React.Fragment>
            <InputFieldBlock
              id="webhook-url"
              label={inputLabel}
              hint={inputHint}
              value={webhookUrl}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setWebhookUrl(e.target.value)
              }
            />
            <Spacer size={5} />
          </React.Fragment>
        )}

        {hasSpecificEvents ? (
          <React.Fragment>
            <Text size="M">
              {notificationDeploysText.messages.specificEventsIntro}
            </Text>
            <ul>
              {specificEvents
                .map(getNotificationDeploysStatus)
                .map(eventText => (
                  <li key={eventText} css={listItemCss}>
                    <Text as="span">{eventText}</Text>
                  </li>
                ))}
            </ul>
          </React.Fragment>
        ) : (
          <NotificationEventField
            id="event"
            name="event"
            value={selectedEvent}
            onChange={e =>
              setSelectedEvent(e.target.value as NotificationEventType | ``)
            }
          />
        )}

        {error && <ErrorAlert>{error.message}</ErrorAlert>}
      </StyledPanelBodySection>
      <StyledPanelActions>
        <Button onClick={onClose} disabled={loading} variant="SECONDARY">
          {uiText.actions.cancel}
        </Button>

        <Button
          disabled={disableSubmit}
          rightIcon={<MdArrowForward />}
          onClick={() => {
            if (hasSpecificEvents) {
              onSubmit(specificEvents)
            } else if (selectedEvent) {
              onSubmit([selectedEvent])
            }
          }}
          loading={loading}
        >
          {uiText.actions.save}
        </Button>
      </StyledPanelActions>
    </StyledPanelBoilerplate>
  )
}
